<template>
    <div>
        <b-form-row>
            <b-col>
                <h5>Purchase Review</h5>
            </b-col>
        </b-form-row>
        <hr>
        <b-form-row class="review-section">
            <b-col>
                <span>Reviewer Comments</span>
                <br>
                <b-form-textarea
                    v-model="form.actor_comment"
                    :disabled="!isPurchaseReviewable"
                    :rows="5"
                    placeholder="Comments are required to enable 'Accept' and 'Reject' buttons below"
                />
            </b-col>
        </b-form-row>

        <template v-if="isPurchaseReviewable">
            <b-form-row class="review-section">
                <b-col cols="3">
                    <b-button
                        id="popover-accept-button"
                        variant="outline-success"
                        :loading="isAccepting"
                        :disabled="hasNoComment"
                        @click="onClickAccept('popover-accept-button')"
                    >
                        <fa-icon :icon="['fal', 'thumbs-up']" />
                        {{ acceptButtonLabel }}
                    </b-button>
                </b-col>
                <b-col cols="3">
                    <b-button
                        id="popover-reject-button"
                        variant="outline-danger"
                        :loading="isRejecting"
                        :disabled="hasNoComment"
                        @click="onClickReject('popover-reject-button')"
                    >
                        <fa-icon :icon="['fal', 'thumbs-down']" />
                        {{ rejectButtonLabel }}
                    </b-button>
                </b-col>
            </b-form-row>

            <!-- Accept Proposal Pop -->
            <b-popover id="popover-reject" target="popover-accept-button">
                <template slot="title" class="title-class">
                    <strong>Accept Purchase</strong>
                </template>
                <template>
                    <b-form-row>
                        <b-col>
                            <p>
                                Click the
                                <strong class="text-success">Accept</strong>
                                button, if you approve of the contents of this Purchase and wish to send
                                it to the next reviewer's inbox.
                            </p>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col ref="acceptButton" cols="6">
                            <b-button
                                size="sm"
                                block
                                class="cancel-button"
                                @click="hidePopover('popover-accept-button')"
                            >
                                <strong>Cancel</strong>
                            </b-button>
                        </b-col>
                        <b-col cols="6">
                            <b-button size="sm"
                                      block
                                      variant="success"
                                      @click="acceptPurchase"
                            >
                                <strong>ACCEPT</strong>
                            </b-button>
                        </b-col>
                    </b-form-row>
                </template>
            </b-popover>

            <!-- Reject Proposal Pop -->
            <b-popover id="popover-reject" target="popover-reject-button">
                <template slot="title">
                    <strong>Reject Purchase</strong>
                </template>
                <template slot="default">
                    <b-form-row>
                        <b-col>
                            <p>
                                Click the
                                <strong class="text-danger">Reject</strong>
                                button, if you found problems with the contents of this Purchase and
                                wish to send it back to the applicant's inbox.
                            </p>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col cols="6">
                            <b-button
                                size="sm"
                                block
                                class="cancel-button"
                                @click="hidePopover('popover-reject-button')"
                            >
                                <strong>Cancel</strong>
                            </b-button>
                        </b-col>
                        <b-col cols="6">
                            <b-button size="sm"
                                      block
                                      variant="danger"
                                      @click="rejectPurchase"
                            >
                                <strong>REJECT</strong>
                            </b-button>
                        </b-col>
                    </b-form-row>
                </template>
            </b-popover>
        </template>

        <b-form-row v-else class="review-section">
            <b-badge v-if="hasBeenAccepted" variant="success" style="font-size: 1.1em;">
                <fa-icon :icon="['fal', 'thumbs-up']" />
                {{ acceptButtonLabel }}
            </b-badge>

            <b-badge v-else-if="hasBeenRejected" variant="danger" style="font-size: 1.1em;">
                <fa-icon :icon="['fal', 'thumbs-down']" />
                {{ rejectButtonLabel }}
            </b-badge>

            <b-badge v-else variant="info" style="font-size: 1.1em;">
                Awaiting Review
            </b-badge>
        </b-form-row>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { Log } from '$Shared/@arc/vue-arc';

export default {
    name: 'PurchaseReview',

    props: {
        formDisabled: { type: Boolean, default: false },
        applicationId: { type: [String, Number], required: true },
        proposalId: { type: [String, Number], required: true },
    },

    data() {
        return {
            form: {
                actor_comment: null,
            },
            actionTaken: null,
            isAccepting: false,
            isRejecting: false,
            backgroundColor: '#006438',
            dropdownOptions: [],
        };
    },

    computed: {
        ...mapState('purchase/form', {
            purchaseForm: (state) => state,
        }),

        ...mapGetters({
            isPurchaseReviewable: 'purchase/isReviewable',
            purchaseActionTaken: 'purchase/actionTaken',
        }),

        payload() {
            return {
                ...this.form,
                applicationId: this.applicationId,
                proposalId: this.proposalId,
                costId: this.purchaseForm.cost_id,
                purchaseId: this.purchaseForm.purchase_id,
            };
        },

        hasBeenAccepted() {
            return this.actionTaken === 'accept' && !this.isPurchaseReviewable;
        },

        hasBeenRejected() {
            return this.actionTaken === 'reject' && !this.isPurchaseReviewable;
        },

        acceptButtonLabel() {
            return this.hasBeenAccepted ? 'Purchase Accepted' : 'Accept this Purchase';
        },

        rejectButtonLabel() {
            return this.hasBeenRejected ? 'Purchase Rejected' : 'Reject this Purchase';
        },

        purchaseReviewStatusBadgeOptions() {
            switch (this.actionTaken) {
                case 'accept':
                    return { status: 'Accepted', variant: 'success' };
                case 'reject':
                    return { status: 'Rejected', variant: 'danger' };
                default:
                    return { status: 'Awaiting Review', variant: 'info' };
            }
        },

        hasNoComment() {
            return this.form.actor_comment === null || this.form.actor_comment === '';
        },
    },

    created() {
        if (!this.isPurchaseReviewable) {
            this.actionTaken = this.purchaseActionTaken;
        }
    },

    methods: {
        ...mapActions(['fetchRolesForOa', 'assignToUser']),

        async acceptPurchase() {
            this.actionTaken = 'accept';
            this.isAccepting = true;

            const response = await this.$store.dispatch('purchase/accept', {
                ...this.payload,
            });
            this.isAccepting = false;
            Log.debug(response, 'COMPONENT:' + this.name + ' -> ACCEPT PURCHASE response: ');

            this.hideLocalPopovers();
        },

        async rejectPurchase() {
            this.actionTaken = 'reject';
            this.isRejecting = true;

            const response = await this.$store.dispatch('purchase/reject', {
                ...this.payload,
            });
            this.isRejecting = false;
            Log.debug(response, 'COMPONENT:' + this.name + ' -> REJECT PURCHASE response: ');

            this.hideLocalPopovers();
        },

        hideLocalPopovers() {
            this.$root.$emit('bv::hide::popover');
        },

        hidePopover(popoverId) {
            this.$root.$emit('bv::hide::popover', popoverId);
        },

        showPopover(popoverId) {
            this.$emit('bv::show::popover', popoverId);
        },

        onClickAccept(target) {
            this.hideLocalPopovers();
            this.showPopover(target);
        },

        onClickReject(target) {
            this.hideLocalPopovers();
            this.showPopover(target);
        },
    },
};
</script>

<style lang="scss">
@import '$StyleVars';

h5 {
    font-size: 1.5rem;
    text-align: left;
    font-weight: bold;
    color: $ace-purple;
}

.cancel-button {
    background-color: $cancel-button-color;
    color: $edit-button-color;
    border-color: transparent;

    &:hover {
        background-color: $edit-button-color;
        color: $cancel-button-color;
        border-color: $cancel-button-color;
    }
}

.field-labels {
  font-size: 1rem;
  font-weight: bold;
  text-align: left;
  color: $ace-purple;
  margin-bottom: .25rem;
}

.review-section {
    margin: 10px 0;
    justify-content: space-evenly;
}

.review-section-header {
    font-size: 18px;
    font-weight: bold;
}
</style>
